'use client'

import { useState, useEffect } from 'react'
import { useExtracted } from 'next-intl'
import { useRouter } from '@/i18n/navigation'
import { Sparkles, CheckCircle2 } from '@/components/ui/icons'
import { EmailSignupForm } from '@/components/marketing'
import { BACKGROUND_VIDEOS } from '@/lib/marketing-config'
import { useAuth } from '@/hooks/useAuth'
import { VideoCreditBadge } from '@/components/marketing/VideoCreditBadge'
import { isInTranslatorIframe } from '@/lib/i18n/inTranslatorIframe'
import { useInTranslatorIframe } from '@/lib/i18n/useInTranslatorIframe'

// Select once at module load time (client-side only, SSR will use index 0)
const getRandomVideoIndex = () => Math.floor(Math.random() * BACKGROUND_VIDEOS.length)

export function WaitlistContent() {
  const t = useExtracted()
  const router = useRouter()
  const { isAuthenticated, isAnonymous, isLoading } = useAuth()
  const inTranslatorIframe = useInTranslatorIframe()
  const [isVisible, setIsVisible] = useState(false)
  // null = not yet mounted (SSR), number = selected index
  const [videoIndex, setVideoIndex] = useState<number | null>(null)

  useEffect(() => {
    if (isInTranslatorIframe()) return
    // Redirect logged-in users to projects
    if (!isLoading && isAuthenticated && !isAnonymous) {
      router.replace('/projects')
    }
  }, [isLoading, isAuthenticated, isAnonymous, router])

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      // Select random video once on mount, never changes after
      setVideoIndex(getRandomVideoIndex())
      setIsVisible(true)
    })

    return () => cancelAnimationFrame(frame)
  }, [])

  // Don't flash content while redirecting logged-in users
  if (!inTranslatorIframe) {
    if (isLoading) return null
    if (isAuthenticated && !isAnonymous) return null
  }

  const selectedVideo = videoIndex !== null ? BACKGROUND_VIDEOS[videoIndex] : null
  const waitlistBenefits = [
    {
      title: t('Early access'),
      description: t("Generate free shots with the world's best GenAI models — in exchange for your feedback."),
    },
    {
      title: t('Shape the future'),
      description: t("Your feedback directly influences Martini's development and feature roadmap."),
    },
    {
      title: t('Pioneer new storytelling'),
      description: t('Be part of the creative community defining the next generation of filmmaking tools.'),
    },
  ]

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-black">
      {/* Video Background */}
      <div className="absolute inset-0 h-full w-full bg-black">
        {selectedVideo && (
          <video
            key={selectedVideo.src}
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 h-full w-full scale-110 object-cover"
            style={{ objectPosition: 'center center' }}
            aria-label={t('Background video showcasing AI filmmaking')}
          >
            <source src={selectedVideo.src} type="video/mp4" />
          </video>
        )}
      </div>

      {/* Gradient Overlays - lighter to show video */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/55" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />

      {/* Background Effects */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
      </div>

      {/* Video Credit Badge - Hidden on mobile */}
      {selectedVideo && (
        <div className="absolute bottom-6 end-6 z-20 hidden md:bottom-8 md:end-8 md:block">
          <VideoCreditBadge video={selectedVideo} />
        </div>
      )}

      {/* Hero Section with Form */}
      <section className="relative z-10 flex min-h-screen items-center justify-center px-6 pt-24 pb-20 md:pt-32">
        <div className="w-full max-w-5xl">
          <div className="grid gap-4 lg:grid-cols-2 lg:gap-16 lg:items-start">
            {/* Left Column - Headline & Intro (appears first on mobile) */}
            <div
              className={`order-1 space-y-8 text-start transition-all duration-1000 lg:order-1 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
            >
              {/* Badge */}
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur-xl">
                <Sparkles className="h-3.5 w-3.5 text-white/70" />
                <span className="text-xs font-medium tracking-wide text-white/80">{t('Early Access')}</span>
              </div>

              {/* Main Headline */}
              <header className="space-y-6">
                <h1 className="text-5xl font-bold leading-[1.1] tracking-tight text-white sm:text-6xl md:text-7xl lg:text-8xl">
                  <span className="block">{t('Be among')}</span>
                  <span className="block">{t('the first.')}</span>
                </h1>

                <p className="max-w-xl text-xl font-light leading-relaxed text-white/85 sm:text-2xl md:text-3xl">
                  {t(
                    "Martini is in early access. We're inviting filmmakers every week to shape the future of generative cinema.",
                  )}
                </p>
              </header>

              {/* Benefits - Desktop only (stays with left column on desktop) */}
              <div className="hidden space-y-4 pt-4 lg:block">
                {waitlistBenefits.map((benefit, index) => (
                  <div
                    key={benefit.title}
                    className="flex items-start gap-3 transition-all duration-1000"
                    style={{
                      transitionDelay: `${index * 100}ms`,
                      opacity: isVisible ? 1 : 0,
                      transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
                    }}
                  >
                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-white/60" />
                    <div>
                      <h3 className="mb-1 text-base font-semibold tracking-tight text-white md:text-lg">
                        {benefit.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-white/70 md:text-base">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - Form Card (appears second on mobile, after headline) */}
            <div
              className={`order-2 transition-all duration-1000 delay-300 lg:order-2 lg:sticky lg:top-24 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
            >
              <div className="relative group">
                {/* Glow effect */}
                <div className="absolute -inset-0.5 rounded-3xl bg-gradient-to-r from-white/20 via-white/10 to-transparent opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-100" />

                {/* Form Card */}
                <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 via-white/[0.03] to-transparent p-8 backdrop-blur-xl md:p-10 lg:p-12">
                  {/* Card Header */}
                  <div className="mb-8 space-y-3">
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur-xl">
                      <span className="text-base">🎁</span>
                      <span className="text-sm font-medium text-white/90">{t('Early Access Benefits')}</span>
                    </div>
                    <h2 className="text-2xl font-bold tracking-tight text-white md:text-3xl">
                      {t('Join Early Access')}
                    </h2>
                    <p className="text-base font-light text-white/70 md:text-lg">
                      {t('Enter your email to secure your spot for early access.')}
                    </p>
                  </div>

                  {/* Form */}
                  <EmailSignupForm
                    source="waitlist"
                    buttonText={t('Join Early Access')}
                    loadingText={t('Joining...')}
                    className="space-y-5"
                  />

                  {/* Trust Indicators */}
                  <div className="mt-8 flex flex-wrap items-center gap-4 text-xs text-white/60 md:text-sm">
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4" />
                      <span>{t('No spam, ever')}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4" />
                      <span>{t('Free early access')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Benefits - Mobile only (appears third on mobile, below form) */}
            <div
              className={`order-3 space-y-4 text-start transition-all duration-1000 lg:hidden ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}
            >
              {waitlistBenefits.map((benefit, index) => (
                <div
                  key={benefit.title}
                  className="flex items-start gap-3 transition-all duration-1000"
                  style={{
                    transitionDelay: `${index * 100}ms`,
                    opacity: isVisible ? 1 : 0,
                    transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
                  }}
                >
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-white/60" />
                  <div>
                    <h3 className="mb-1 text-base font-semibold tracking-tight text-white md:text-lg">
                      {benefit.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-white/70 md:text-base">{benefit.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
